import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// دالة مساعدة للحصول على التوكن
const getAuthToken = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
    
    let token = storedUser?.token;
    if (!token) {
      token = storedUser?.data?.token;
    }
    if (!token) {
      token = storedUser?.user?.token;
    }
    if (!token) {
      token = storedUser?.result?.token;
    }

    return token;
  } catch (error) {
    console.error("❌ Error getting token:", error);
    return null;
  }
};

// Fetch Messages
export const fetchMessages = createAsyncThunk(
  "messages/fetchMessages",
  async (page = 1, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/messages?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحميل الرسائل";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch single Message
export const fetchMessage = createAsyncThunk(
  "messages/fetchMessage",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/messages/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Message fetched successfully",
        data: response.data?.data || response.data,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحميل الرسالة";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add Message
export const addMessage = createAsyncThunk(
  "messages/addMessage",
  async (messageData, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/messages`,
        {
          content_en: messageData.content_en,
          content_ar: messageData.content_ar,
          type: messageData.type
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Message added successfully",
        data: response.data?.data || response.data,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في إضافة الرسالة";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update Message
export const updateMessage = createAsyncThunk(
  "messages/updateMessage",
  async ({ id, messageData }, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/messages/${id}/update`,
        {
          content_en: messageData.content_en,
          content_ar: messageData.content_ar,
          type: messageData.type
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Message updated successfully",
        data: response.data?.data || response.data,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحديث الرسالة";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete Message
export const deleteMessage = createAsyncThunk(
  "messages/deleteMessage",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.delete(
        `${BASE_URL}/dashboard/messages/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Message deleted successfully",
        id: id,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في حذف الرسالة";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  data: [],
  meta: null,
  selectedMessage: null,
  loading: false,
  error: null,
  addLoading: false,
  addError: null,
  addSuccessMessage: null,
  updateLoading: false,
  updateError: null,
  updateSuccessMessage: null,
  deleteLoading: false,
  deleteError: null,
  deleteSuccessMessage: null,
};

const messageSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    clearMessageState: (state) => {
      state.error = null;
      state.addError = null;
      state.addSuccessMessage = null;
      state.updateError = null;
      state.updateSuccessMessage = null;
      state.deleteError = null;
      state.deleteSuccessMessage = null;
    },
    clearSelectedMessage: (state) => {
      state.selectedMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Messages
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || action.payload;
        state.meta = action.payload.meta || null;
        state.error = null;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch single Message
      .addCase(fetchMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMessage = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add Message
      .addCase(addMessage.pending, (state) => {
        state.addLoading = true;
        state.addError = null;
        state.addSuccessMessage = null;
      })
      .addCase(addMessage.fulfilled, (state, action) => {
        state.addLoading = false;
        state.addSuccessMessage = action.payload.message;
        state.addError = null;
        if (action.payload.data) {
          state.data.push(action.payload.data);
        }
      })
      .addCase(addMessage.rejected, (state, action) => {
        state.addLoading = false;
        state.addError = action.payload;
        state.addSuccessMessage = null;
      })
      
      // Update Message
      .addCase(updateMessage.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccessMessage = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccessMessage = action.payload.message;
        state.updateError = null;
        if (action.payload.data) {
          const index = state.data.findIndex(message => message.id === action.payload.data.id);
          if (index !== -1) {
            state.data[index] = action.payload.data;
          }
        }
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccessMessage = null;
      })
      
      // Delete Message
      .addCase(deleteMessage.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccessMessage = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccessMessage = action.payload.message;
        state.deleteError = null;
        state.data = state.data.filter(message => message.id !== action.payload.id);
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccessMessage = null;
      });
  },
});

export const { clearMessageState, clearSelectedMessage } = messageSlice.actions;
export default messageSlice.reducer; 